import {
  applyCheckedPositionTrade,
  type CheckedPositionAccountingResult,
  type CheckedPositionAccountingState,
  type PoolMathResult,
  unavailable,
} from '@morfi/market-math';
import type { PositionAccountingDto } from '../types.js';

const CANONICAL_UNSIGNED_INTEGER = /^(?:0|[1-9]\d*)$/;
const CANONICAL_SIGNED_INTEGER = /^(?:0|-?[1-9]\d*)$/;

export interface CheckedSellPositionAccountingInput {
  positionAccounting: PositionAccountingDto | null;
  tokenAmountD6: string;
  netQuoteProceedsD6: string;
  isAmm: boolean;
}

/** Strict DTO adapter for the canonical checked position-accounting API. */
export function computeCheckedSellPositionAccounting(
  input: CheckedSellPositionAccountingInput,
): PoolMathResult<CheckedPositionAccountingResult> {
  const state = parsePositionAccounting(input.positionAccounting);
  const tokenAmountD6 = parseCanonicalUnsigned(input.tokenAmountD6);
  const netQuoteProceedsD6 = parseCanonicalUnsigned(input.netQuoteProceedsD6);
  if (state === null || tokenAmountD6 === null || netQuoteProceedsD6 === null) {
    return unavailable('malformed_position_accounting');
  }

  return applyCheckedPositionTrade(state, {
    direction: 'sell',
    tokenAmountD6,
    netQuoteProceedsD6,
    isAmm: input.isAmm,
  });
}

function parsePositionAccounting(
  dto: PositionAccountingDto | null,
): CheckedPositionAccountingState | null {
  if (dto === null) return null;
  const balanceD6 = parseCanonicalUnsigned(dto.balanceD6);
  const remainingCostBasisNetD6 = parseCanonicalUnsigned(dto.remainingCostBasisNetD6);
  const claimRightsBalanceD6 = parseCanonicalUnsigned(dto.claimRightsBalanceD6);
  const realizedPnlD6 = parseCanonicalSigned(dto.realizedPnlD6);
  const realizedCostBasisD6 = parseCanonicalUnsigned(dto.realizedCostBasisD6);
  if (
    balanceD6 === null ||
    remainingCostBasisNetD6 === null ||
    claimRightsBalanceD6 === null ||
    realizedPnlD6 === null ||
    realizedCostBasisD6 === null
  ) {
    return null;
  }
  return {
    balanceD6,
    remainingCostBasisNetD6,
    claimRightsBalanceD6,
    realizedPnlD6,
    realizedCostBasisD6,
  };
}

function parseCanonicalUnsigned(value: string): bigint | null {
  return CANONICAL_UNSIGNED_INTEGER.test(value) ? BigInt(value) : null;
}

function parseCanonicalSigned(value: string): bigint | null {
  return CANONICAL_SIGNED_INTEGER.test(value) ? BigInt(value) : null;
}
